import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Stethoscope, User, Lock, Eye, EyeOff } from 'lucide-react';

const Login = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const moduleId = searchParams.get('module');

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    const result = await login(formData.email, formData.password);
    
    if (result.success) {
      // Redirect to specific module if moduleId is provided
      if (moduleId) {
        navigate(`/module/${moduleId}`);
      } else {
        // Redirect based on user role
        if (result.user.role === 'admin') {
          navigate('/admin');
        } else {
          navigate('/welcome');
        }
      }
    } else {
      setError(result.error);
      setIsLoading(false);
    }
  };

  const demoCredentials = [
    { role: 'Admin', email: 'admin@trumerit.org', password: 'admin123' },
    { role: 'Employee', email: 'employee@trumerit.org', password: 'employee123' },
    { role: 'User', email: 'leo@trumerit.org', password: 'leo123' }
  ];

  return (
    <div className="login-container">
      <div className="login-header-top">
        <h2 className="portal-title">MeritIQ</h2>
      </div>
      <div className="login-card">
        <div className="trumerit-logo-container">
          <img src="/Trumeritlogo.png" alt="TruMerit Logo" className="trumerit-logo" />          
        </div>
        <div className="login-header">
          <p className="login-title">Sign in</p>
          {moduleId && (
            <div className="module-redirect-notice">
              <p className="redirect-text">
                You're being directed to a specific training module. 
                Please log in to continue.
              </p>
            </div>
          )}
        </div>

        <form onSubmit={handleSubmit} className="login-form">
          <div className="form-group">
            <label htmlFor="email" className="form-label">
              <User size={16} />
              Email Address
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className="form-input"
              placeholder="Enter your email"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="password" className="form-label">
              <Lock size={16} />
              Password
            </label>
            <div className="password-input-container">
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                className="form-input"
                placeholder="Enter your password"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="password-toggle"
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>

          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={isLoading}
            className="login-button"
          >
            {isLoading ? 'Signing In...' : 'Sign In'}
          </button>
        </form>
      </div>

      <div className="demo-credentials">
        <h3 className="demo-title">Demo Credentials</h3>
        <div className="demo-list">
          {demoCredentials.map((cred, index) => (
            <div key={index} className="demo-item">
              <div className="demo-role">{cred.role}</div>
              <div className="demo-email">{cred.email}</div>
              <div className="demo-password">{cred.password}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Login;
